import { useCallback, useMemo, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import TerminalIcon from '@mui/icons-material/Terminal';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { MachineDeviceSummary } from '../../api/types';
import {
  useMyPermissions,
  useMachineDevicesList,
  HAT_CONSOLE_PERMISSION,
} from '../../api/queries';
import { FirmwareSkewPanel, FIRMWARE_PROMOTE_PERMISSION } from './FirmwareSkewPanel';
import { MachineDeviceFirmwareDialog } from './MachineDeviceFirmwareDialog';
import { MachineDeviceConsoleDialog } from './MachineDeviceConsoleDialog';

/**
 * ESP32 Deployment Plan §3 M1 — flat list of ESP32-P4 HATs (machine
 * devices) in the user's scope. ARMS sees all; operator users see HATs
 * whose SC is bound to their operator.
 */
export default function MachineDevicesPage() {
  usePageTitle('IoT devices (HATs)');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const { queryKey, listFn } = useMachineDevicesList();

  // Per-HAT running-firmware detail dialog (M8). Opened from a row action;
  // the fetch is deferred until the dialog is open.
  const [firmwareFor, setFirmwareFor] = useState<{ id: string; qrId: string | null } | null>(null);

  // Remote HAT Console (P0, Mode A). Opened from a row action; the live session
  // is created only while the dialog is open and torn down on close.
  const [consoleFor, setConsoleFor] = useState<{ id: string; qrId: string | null } | null>(null);

  const columns = useMemo<GridColDef<MachineDeviceSummary>[]>(() => [
    { field: 'qrId', headerName: 'IoT QR', width: 220,
      renderCell: (p) => p.row.qrId
        ? <code>{p.row.qrId}</code>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    { field: 'status', headerName: 'Status', width: 150,
      renderCell: (p) => <StatusChip status={p.row.status} /> },
    // ESP32 Deployment Plan §3 M5 — gateway/runtime state tracked by the SC's
    // MachineOrchestrator. dormant until the SC starts a Route.MachineGateway
    // container for the HAT (M7); online once the SAS handshake completes.
    { field: 'gatewayStatus', headerName: 'Gateway', width: 130,
      renderCell: (p) => <GatewayStatusChip status={p.row.gatewayStatus} /> },
    { field: 'siteControllerId', headerName: 'Site controller', width: 180,
      renderCell: (p) => p.row.siteControllerId
        ? <code style={{ fontSize: '0.75rem' }}>{p.row.siteControllerId.slice(0, 8)}…</code>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    { field: 'machineAssetNumber', headerName: 'Machine', width: 180,
      renderCell: (p) => p.row.machineAssetNumber
        ? <Typography variant="body2">{p.row.machineAssetNumber}</Typography>
        : p.row.machineId
          ? <code style={{ fontSize: '0.75rem' }}>{p.row.machineId.slice(0, 8)}…</code>
          : <Typography variant="body2" color="text.secondary">unbound</Typography> },
    { field: 'firmwareVersionAtRegister', headerName: 'Firmware', width: 130,
      renderCell: (p) => p.row.firmwareVersionAtRegister
        ? <Typography variant="body2">{p.row.firmwareVersionAtRegister}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    { field: 'createdAt', headerName: 'Registered', width: 180,
      valueFormatter: (v: unknown) => fmtDateTime(v) },
    { field: 'lastKnownStatusAt', headerName: 'Last seen', width: 180,
      valueFormatter: (v: unknown) => v ? fmtDateTime(v) : '—' },
  ], []);

  const quickFilters = useMemo<QuickFilter<MachineDeviceSummary>[]>(() => [
    { label: 'All',                filter: () => true },
    { label: 'Ready for install',  filter: (d) => d.status === 'ready_for_install' },
    { label: 'Commissioning',      filter: (d) => d.status === 'commissioning' },
    { label: 'In service',         filter: (d) => d.status === 'in_service' },
    { label: 'Offline',            filter: (d) => d.status === 'offline' },
    { label: 'Defective',          filter: (d) => d.status === 'defective' },
  ], []);

  const exportColumns = useMemo<ExportColumn<MachineDeviceSummary>[]>(() => [
    { key: 'qrId',              label: 'IoT QR' },
    { key: 'status',            label: 'Status' },
    { key: 'gatewayStatus',     label: 'Gateway' },
    { key: 'siteControllerId',  label: 'Site controller' },
    { key: 'machineAssetNumber', label: 'Machine' },
    { key: 'firmwareVersionAtRegister', label: 'Firmware' },
    { key: 'createdAt',         label: 'Registered', format: (v) => fmtDateTime(v) },
    { key: 'lastKnownStatusAt', label: 'Last seen',  format: (v) => v ? fmtDateTime(v) : '—' },
  ], []);

  const actions = useMemo<RowAction<MachineDeviceSummary>[]>(() => [
    {
      label: 'Firmware',
      icon: <MemoryIcon fontSize="small" />,
      onClick: (row) => setFirmwareFor({ id: row.id, qrId: row.qrId }),
    },
    // Remote HAT Console (P0). Gated on the console permission (hidden when the
    // user lacks it, per PORTAL_FORM_RULES §11). Available whenever the ESP32 is
    // up — the console (Mode A hat_diag) is a NETWORK/link diagnostic, independent
    // of SAS/gateway state; it's precisely the tool for diagnosing a silent
    // gateway. So we do NOT gate on gatewayStatus (which needs SAS). We enable it
    // for any COMMISSIONED HAT and let RMS StartSession be the authoritative rail:
    // it opens the session when esp32_online and returns hat_offline otherwise.
    {
      label: 'Console',
      icon: <TerminalIcon fontSize="small" />,
      visible: () => has(HAT_CONSOLE_PERMISSION),
      disabled: (row) => row.status !== 'installed',
      disabledReason: () =>
        'Console is available for a commissioned (installed) HAT once its ESP32 is up.',
      onClick: (row) => setConsoleFor({ id: row.id, qrId: row.qrId }),
    },
  ], [has]);

  if (!has('provisioning.machine-devices.view')) {
    return (
      <Typography color="text.secondary">
        You don't have permission to view IoT devices.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4">IoT devices (HATs)</Typography>
      <Typography variant="body2" color="text.secondary">
        Per-machine ESP32-P4 HATs. Each row is one HAT registered at the
        factory bench and tracked through its lifecycle:
        ready_for_install → commissioning → installed → in_service.
      </Typography>

      <FirmwareSkewPanel canPromote={has(FIRMWARE_PROMOTE_PERMISSION)} />

      <RouteDataGrid<MachineDeviceSummary>
        entityName="IoT devices"
        queryKey={queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={[{ kind: 'text', key: 'qrId', label: 'IoT QR' }]}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        actions={actions}
        reportTitle="ARMS — IoT devices"
        emptyMessage="No IoT devices registered yet."
      />

      <MachineDeviceFirmwareDialog
        open={firmwareFor !== null}
        machineDeviceId={firmwareFor?.id ?? null}
        qrLabel={firmwareFor?.qrId ?? null}
        onClose={() => setFirmwareFor(null)}
      />

      <MachineDeviceConsoleDialog
        open={consoleFor !== null}
        machineDeviceId={consoleFor?.id ?? null}
        qrLabel={consoleFor?.qrId ?? null}
        onClose={() => setConsoleFor(null)}
      />
    </Stack>
  );
}

function StatusChip({ status }: { status: string }) {
  const color =
    status === 'in_service'        ? 'success' :
    status === 'installed'         ? 'success' :
    status === 'commissioning'     ? 'info' :
    status === 'ready_for_install' ? 'default' :
    status === 'offline'           ? 'warning' :
    status === 'defective'         ? 'error' :
    status === 'voided'            ? 'error' :
    'default';
  return <Chip size="small" label={status} color={color as 'success' | 'info' | 'error' | 'default' | 'warning'} />;
}

/**
 * ESP32 Deployment Plan §3 M5 — gateway status chip. Mirrors StatusChip
 * but colours the runtime/container state independently of the device
 * lifecycle state.
 */
function GatewayStatusChip({ status }: { status: string }) {
  const color =
    status === 'online'   ? 'success' :
    status === 'starting' ? 'info' :
    status === 'silent'   ? 'warning' :
    status === 'offline'  ? 'error' :
    'default'; // dormant
  return <Chip size="small" label={status} color={color as 'success' | 'info' | 'error' | 'default' | 'warning'} />;
}
